import { useEffect, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { Constraints, RuleGroup } from "@trackmark/core";
import { isRunnable } from "@trackmark/core";
import { api, endpoints } from "../api.js";
import { useResource } from "../useResource.js";
import { Failed, Loading } from "./Loading.js";
import { Playlists, type GenerateResult } from "./Playlists.js";

const EMPTY: RuleGroup = { kind: "group", join: "and", children: [] };
const DEFAULTS: Constraints = { limit: 50, maxPerArtist: 2, order: "score", seed: 1 };

/**
 * Playlists, wired.
 *
 * `Raise cap to 3` applies the relaxation the report priced and regenerates — it does not
 * fabricate the longer list from the one already on screen. The button's promise ("would yield 50
 * of 50") came from an actual run, and honouring it with a second actual run is the only way the
 * number on the button and the number that appears can be the same.
 */
export function PlaylistsScreen() {
  const playlists = useResource(() => endpoints.playlists(), []);
  // `?from=<id>` — Regenerate on a saved playlist hands its recipe over here.
  const { from } = useSearch({ from: "/playlists" });
  const navigate = useNavigate();

  /** Leaves the generator and drops `?from=`, so a reload does not re-open somebody else's recipe. */
  const closeBuilder = () => {
    setBuilding(false);
    if (from !== undefined) void navigate({ to: "/playlists", search: {} });
  };

  const [building, setBuilding] = useState(false);
  const [name, setName] = useState("");
  const [rules, setRules] = useState<RuleGroup>(EMPTY);
  const [constraints, setConstraints] = useState<Constraints>(DEFAULTS);
  const [hints, setHints] = useState<{ excludes: number }[]>([]);
  const [filterMatches, setFilterMatches] = useState<number | null>(null);
  const [result, setResult] = useState<GenerateResult | null>(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  /**
   * Loads a saved playlist's recipe and generates once.
   *
   * The result is shown before anything is written, exactly as a fresh generation is: the outcome
   * line, the binding constraint, and Keep. Regenerating writes a NEW playlist when you keep it —
   * the one it came from is a snapshot and stays as it was.
   */
  useEffect(() => {
    if (from === undefined) return;
    let live = true;
    void api.get<{ name: string; rules: RuleGroup; constraints: Constraints }>(`/api/playlists/${from}`).then((saved) => {
      if (!live) return;
      setName(saved.name);
      setRules(saved.rules);
      setConstraints(saved.constraints);
      setBuilding(true);
      setGenerating(true);
      void endpoints
        .generatePlaylist(saved.rules, saved.constraints)
        .then((r) => live && setResult(r))
        .finally(() => live && setGenerating(false));
    });
    return () => {
      live = false;
    };
  }, [from]);

  useEffect(() => {
    if (!building || !isRunnable(rules)) return;
    let live = true;
    const timer = setTimeout(() => {
      void endpoints.listPreview(rules, 0).then((r) => {
        if (!live) return;
        setFilterMatches(r.total);
        setHints(r.hints);
      });
    }, 140);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [rules, building]);

  const run = (next: Constraints) => {
    setGenerating(true);
    void endpoints
      .generatePlaylist(rules, next)
      .then(setResult)
      .finally(() => setGenerating(false));
  };

  if (playlists.error) return <Failed error={playlists.error} retry={playlists.reload} />;
  if (playlists.loading) return <Loading what="playlists" />;

  return (
    <Playlists
      playlists={playlists.data?.playlists ?? []}
      onDelete={(id) => void endpoints.deletePlaylist(id).then(() => playlists.reload())}
      building={building}
      onNew={() => {
        setBuilding(true);
        setResult(null);
        setName("");
        setRules(EMPTY);
        setConstraints(DEFAULTS);
      }}
      onCloseBuilder={closeBuilder}
      name={name}
      onName={setName}
      rules={rules}
      onRules={setRules}
      hints={hints}
      filterMatches={filterMatches}
      constraints={constraints}
      onConstraints={setConstraints}
      result={result}
      generating={generating}
      onGenerate={() => run(constraints)}
      onRelax={() => {
        if (result?.report.relaxed == null) return;
        const { name: which, to } = result.report.relaxed;
        const next: Constraints = { ...constraints };
        if (which === "maxPerArtist") next.maxPerArtist = to;
        else if (which === "maxPerAlbum") next.maxPerAlbum = to;
        else if (which === "minScore") next.minScore = to;
        else if (which === "excludePlayedWithin") next.excludePlayedWithinDays = to === 0 ? null : to;
        setConstraints(next);
        run(next);
      }}
      onKeep={() => {
        if (result === null) return;
        setSaving(true);
        void endpoints
          .savePlaylist(name.trim() === "" ? "Untitled playlist" : name, rules, constraints, result.tracks.map((t) => t.id))
          .then(() => {
            playlists.reload();
            closeBuilder();
          })
          .finally(() => setSaving(false));
      }}
      saving={saving}
    />
  );
}
